/**
 * User Directory Service
 *
 * Resolves a user id → display name on demand (via the System API), with an
 * in-memory cache and in-flight de-duplication. Used to show audit responsible
 * users ("included/changed by …") without blocking the form: callers resolve
 * names asynchronously and render them when ready.
 */
import { rtLogService } from '@rammetec/core';
import { systemApiUserService } from './systemApiUserService.js';
class UserDirectoryService {
    constructor() {
        this.nameCache = new Map();
        this.inflight = new Map();
    }
    /**
     * Resolves display names for many uids in one round-trip when possible.
     * Cached uids are returned from memory; only missing ones hit the System API.
     */
    async getUserNames(uids) {
        const result = new Map();
        const unique = [...new Set(uids.filter(Boolean))];
        if (unique.length === 0)
            return result;
        const missing = [];
        const pending = [];
        for (const uid of unique) {
            if (this.nameCache.has(uid)) {
                const cached = this.nameCache.get(uid);
                if (cached)
                    result.set(uid, cached);
                continue;
            }
            const existing = this.inflight.get(uid);
            if (existing) {
                pending.push(existing.then((name) => {
                    if (name)
                        result.set(uid, name);
                }));
                continue;
            }
            missing.push(uid);
        }
        if (missing.length > 0) {
            const batchRequest = (async () => {
                try {
                    const fetched = await systemApiUserService.getUserNames(missing);
                    for (const [uid, name] of fetched) {
                        this.nameCache.set(uid, name);
                        result.set(uid, name);
                    }
                }
                catch (error) {
                    rtLogService.warn('[UserDirectoryService] Failed to resolve user names batch', {
                        count: missing.length,
                        error,
                    });
                }
                finally {
                    for (const uid of missing) {
                        this.inflight.delete(uid);
                    }
                }
            })();
            for (const uid of missing) {
                const single = batchRequest.then(() => this.nameCache.get(uid));
                this.inflight.set(uid, single);
                pending.push(single.then((name) => {
                    if (name)
                        result.set(uid, name);
                }));
            }
        }
        if (pending.length > 0) {
            await Promise.all(pending);
        }
        return result;
    }
    /**
     * Returns the user's display name (falls back to email, then the id). Cached;
     * concurrent calls for the same id share a single request. Failures are not
     * cached, so a later call can retry.
     */
    async getUserName(uid) {
        if (!uid)
            return undefined;
        const names = await this.getUserNames([uid]);
        return names.get(uid);
    }
    /** Seed the cache (e.g. from a list already fetched) to avoid an extra request. */
    prime(uid, name) {
        if (uid)
            this.nameCache.set(uid, name);
    }
    /** Clear the cache (e.g. on logout). */
    clear() {
        this.nameCache.clear();
        this.inflight.clear();
    }
}
export const userDirectoryService = new UserDirectoryService();
//# sourceMappingURL=userDirectoryService.js.map